import { useQueryClient } from '@tanstack/react-query';
import pick from 'lodash/pick';

import { useLocale } from '../../hooks/useLocale';
import { useOpenOrder } from '../../hooks/useOpenOrder';
import { useOpenOrderWithKYC } from '../../hooks/useOpenOrderWithKYC';
import { useStartGame } from '../../hooks/useStartGame';
import { logToNewRelic } from '../../services/debug';
import { useFlag } from '../../services/launchDarkly';
import { firstEmptySlot } from '../../stores/event-trigger/byo';
import { OfferMode } from '../../stores/event-trigger/types';
import type { BenefitLayoutV3 } from '../../stores/event-trigger/types';
import { isFreeSpinsBundleItem, isMetaGameBundleItem } from '../../stores/rewards/helpers';
import { EnumOpenDialog } from '../../stores/ui/types';
import { useStores } from '../../stores/useStores';
import { isOfferV2PopupType } from '../../utils/events';
import { getPlayerLimitedOfferStock } from '../../utils/limitedOffer';
import { getUserEventLocalizedElement } from '../../utils/localizationUtils';

import { getCategoryBannerAnalyticsProps, sendCategoryBannerEvent } from './getCategoryBannerAnalyticsProps';
import type { CategoryBanner, CategoryBannerCtaType } from './types';

// Mirrors messages/index.tsx's handleOpenPopup for the FEATURE CTA types this ticket supports
// (invite_friends/store/redeem) — same underlying dialogs, same flags.
const FEATURE_VALUES = ['invite_friends', 'store', 'redeem'] as const;
type FeatureValue = (typeof FEATURE_VALUES)[number];

function isFeatureValue(value: string | null): value is FeatureValue {
  return !!value && (FEATURE_VALUES as readonly string[]).includes(value);
}

export function useCategoryBannerCta() {
  const queryClient = useQueryClient();
  const { uiStore, rewardsStore, userSnappyGiftsStore, eventTriggerStore } = useStores();
  const { handleStartGame } = useStartGame();
  const { locale } = useLocale();
  const isInviteFriendsEnabled = useFlag<boolean>('invite_friends_enabled', true);
  const preventPurchase = useFlag('prevent_purchase', false);
  const offerLocalizationEnabled = useFlag('offer_localization', false);
  const { openOrder } = useOpenOrder();
  const openOrderWithKYC = useOpenOrderWithKYC(openOrder);

  const handleFeature = (value: FeatureValue) => {
    switch (value) {
      case 'invite_friends':
        if (!isInviteFriendsEnabled) return;
        void rewardsStore.fetchReferralData();
        uiStore.setOpenDialog({
          dialog: EnumOpenDialog.isOpenInviteFriendsNew,
        });
        break;
      case 'store':
        uiStore.setOpenFullPageDialog(preventPurchase ? 'prevent_purchase' : 'store');
        break;
      case 'redeem':
        uiStore.setOpenFullPageDialog('redeem');
        break;
    }
  };

  const trackClick = (banner: CategoryBanner) => {
    if (banner.ctaType === 'NONE') return;
    void sendCategoryBannerEvent('cta_click', {
      ...getCategoryBannerAnalyticsProps(banner, eventTriggerStore.lastEventTriggerResponse),
      event_action: 'click',
      button_text: banner.layout?.elements?.ACTION_BTN?.text?.toLowerCase(),
    });
  };

  // The banner's own event_trigger_audience/user_event_trigger row is written server-side
  // independently of this session's login-time feed fetch — re-pull now so a just-granted
  // offer/in-house-gift is in the feed before we look for it, instead of waiting for next login.
  // Must go through fetch() (singleFlight-wrapped), not a raw triggerEvent() call: the login-time
  // fetch may still be in flight when the player clicks, and two concurrent triggerEvent calls hit
  // the same server-side per-user lock and the second one 500s ("Another request is already in
  // progress") instead of returning data.
  // `refreshed: false` = the feed refresh failed (or live-ops is off): the cached response may hold an
  // already-used/expired event, and a lookup miss isn't a confirmed miss — callers do nothing.
  const findLiveEventByBenefitKey = async (ctaValue: string) => {
    if ((await eventTriggerStore.fetch()) !== 'READY') return { refreshed: false, event: undefined } as const;
    const data = eventTriggerStore.lastEventTriggerResponse;
    // personalGifts is a separate array in the feed — in-house gifts targeted by a GIFT banner live there.
    const event = [
      ...(data?.currentEventsByTrigger ?? []),
      ...(data?.otherActiveEvents ?? []),
      ...(data?.personalGifts ?? []),
    ].find((triggerEvent) => (triggerEvent.benefit?.key ?? triggerEvent.benefitKey) === ctaValue);
    return { refreshed: true, event } as const;
  };

  type LiveEvent = NonNullable<Awaited<ReturnType<typeof findLiveEventByBenefitKey>>['event']>;

  // Same as the widget / auto-popup queue: a not-yet-started event has its timer started before it's
  // shown. startEventTimer only knows widget-list events — anything else opens as before.
  const startTimerIfNeeded = async (event: LiveEvent): Promise<LiveEvent> => {
    if (event.canDisplay) return event;
    const triggerId = event.isNewEventType ? event.userEventTriggerId : event.eventTrigger.eventTriggerId;
    return (await eventTriggerStore.startEventTimer(triggerId)) ?? event;
  };

  const openSinglePackageOrder = (event: LiveEvent) => {
    const { benefit } = event;
    if (!isOfferV2PopupType(benefit) || benefit.packages.length !== 1) return false;

    const isSoldOut = getPlayerLimitedOfferStock(event)?.isSoldOut ?? false;
    if (isSoldOut) return false;

    const [packageData] = benefit.packages;
    if (
      benefit.byo &&
      firstEmptySlot(benefit.byo, eventTriggerStore.byoPicksOf(event.userEventTriggerId), [packageData.packageId])
    )
      return false;
    const localizedElement = getUserEventLocalizedElement<'2-3-offer', BenefitLayoutV3>({
      locale,
      benefit,
      ff: offerLocalizationEnabled,
    });
    const imageUrl = localizedElement ? localizedElement.imageUrl : benefit.imageUrl;
    const { gcCoin, scCoin, price, bundleItems } = packageData;
    const bundlePayload = bundleItems?.find(isFreeSpinsBundleItem)?.bundlePayload;
    const metaPayload = bundleItems?.find(isMetaGameBundleItem)?.bundlePayload;
    const metagamesRewardsPayloads = bundleItems?.filter(isMetaGameBundleItem).map((item) => item.bundlePayload) || [];
    const isTakeOrLose = benefit.mode === OfferMode.TAKE_OR_LOSE;

    openOrderWithKYC({
      data: {
        imageUrl,
        gcAmount: gcCoin,
        scAmount: scCoin,
        minigameId: bundlePayload?.gameIds?.[0],
        price,
        packageBundles: bundleItems as any[],
        metagamesTokensAmount: metaPayload?.amount,
        packageId: packageData.packageId,
        coinType: bundlePayload?.coinType || metaPayload?.coinType,
        metagamesRewardsPayloads,
        bundleItems: bundleItems || [],
        eventTriggerData: {
          activeEvent: eventTriggerStore.byoActiveEvent(
            pick(event, ['benefit', 'eventTrigger', 'userEventTriggerId', 'canDisplay']),
            [packageData.packageId],
          ),
        },
      },
      purchaseSource: 'offer',
      ...(isTakeOrLose && {
        onClose: () => {
          void eventTriggerStore.openPopup(event);
        },
      }),
    });

    return true;
  };

  const handleOffer = async (ctaValue: string) => {
    const { refreshed, event: foundEvent } = await findLiveEventByBenefitKey(ctaValue);
    if (!refreshed) return;

    if (foundEvent) {
      const event = await startTimerIfNeeded(foundEvent);
      if (openSinglePackageOrder(event)) return;

      // Opens the same EventTriggerDialog StoreOffers/messages use for this event — the actual offer,
      // not the generic Store package grid.
      void eventTriggerStore.openPopup(event, undefined, 'offer');
      return;
    }

    // No live event for this offer — it went inactive/expired between the banner rendering and the
    // click. Per ticket, an OFFER CTA only ever opens the offer popup (never Store) — so there's
    // nothing to open here. Re-pull the banners list so the now-ineligible banner disappears instead
    // of staying up as a dead click.
    void queryClient.invalidateQueries({ queryKey: ['category-banners'] });
  };

  const handleGift = async (ctaValue: string) => {
    // GIFT can target either backbone (see okf features/gift): an in-house gift, delivered via the
    // same event-trigger feed as OFFER, or a Snappy campaign. The admin picker merges both into one
    // dropdown, so try the in-house lookup first. A failed feed refresh still falls through to Snappy,
    // but can't count as a miss, so it never drops the banner on its own.
    const { refreshed, event } = await findLiveEventByBenefitKey(ctaValue);
    if (event) {
      // No PurchaseSource fits a gift claim (that type is purchase-flow specific) — omit it.
      void eventTriggerStore.openPopup(event);
      return;
    }

    // Same staleness issue as OFFER: userSnappyGiftsStore.userSnappyGiftsData is fetched once at
    // login. A gift granted to this player mid-session isn't in it yet — re-pull before looking it up.
    // A failed refresh leaves the login-time array in place — it may hold an already-claimed or expired
    // gift, and a failure isn't a confirmed miss either, so neither open it nor drop the banner.
    if ((await userSnappyGiftsStore.fetch()) !== 'READY') return;
    const gift = userSnappyGiftsStore.userSnappyGiftsData.find((g) => g.campaignId === ctaValue);
    if (gift) {
      userSnappyGiftsStore.openSnappyGiftDialogById(gift.id);
      return;
    }

    // Neither backend has it any more — drop the stale banner, same as OFFER/MESSAGE.
    if (!refreshed) return;
    void queryClient.invalidateQueries({ queryKey: ['category-banners'] });
  };

  const handleMessage = async (ctaValue: string) => {
    const { refreshed, event } = await findLiveEventByBenefitKey(ctaValue);
    if (!refreshed) return;

    if (event) {
      // Same EventTriggerDialog messages/index.tsx renders for a live-ops message popup.
      void eventTriggerStore.openPopup(await startTimerIfNeeded(event));
      return;
    }

    void queryClient.invalidateQueries({ queryKey: ['category-banners'] });
  };

  const handleCtaClick = (banner: CategoryBanner) => {
    trackClick(banner);

    const { ctaType, ctaValue } = banner;
    switch (ctaType as CategoryBannerCtaType) {
      case 'FEATURE':
        if (isFeatureValue(ctaValue)) handleFeature(ctaValue);
        break;
      case 'SLOT': {
        const gameId = Number(ctaValue);
        if (Number.isInteger(gameId) && gameId > 0) {
          handleStartGame(gameId, 'category_banner', banner.slotCoinType ?? undefined);
        } else {
          logToNewRelic('Category banner SLOT CTA has invalid game id', { ctaValue }, 'error');
        }
        break;
      }
      case 'OFFER':
        if (ctaValue) void handleOffer(ctaValue);
        break;
      case 'GIFT':
        if (ctaValue) void handleGift(ctaValue);
        break;
      case 'MESSAGE':
        if (ctaValue) void handleMessage(ctaValue);
        break;
      case 'NONE':
        break;
      default:
        logToNewRelic('Category banner CTA type not supported', { ctaType }, 'error');
    }
  };

  return { handleCtaClick };
}
